// src/contexts/FormListsDraftSyncContext.jsx

import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  safeGetItem,
  safeRemoveItem,
  safeSetItem,
} from '../utils/storageUtils';

/**
 * FormListsDraftSyncContext
 * -----------------------------------------------------------------------------
 * Passive V2 coordination layer for Form Lists spreadsheet drafts.
 *
 * PURPOSE
 *  • Let FormListsLabPage register a dirty-only flush function.
 *  • Let Save Draft / Publish force-flush pending Form Lists spreadsheet edits
 *    before they read dropdownOptions from context/storage.
 *  • Keep the shared Spreadsheet/ engine domain-free.
 *  • Avoid pushing every spreadsheet commit directly into global form contexts.
 *
 * IMPORTANT
 *  • This provider does NOT own spreadsheet rows.
 *  • This provider does NOT store dropdown options.
 *  • This provider does NOT write Firebase.
 *  • This provider reads/writes only lightweight Form Lists working-copy status
 *    through storageUtils so dirty UI state can survive builder route changes.
 *  • This provider does NOT subscribe to Firestore.
 *  • This provider does NOT update React state when a flusher registers.
 *  • Registered flushers decide what is dirty and what must be written.
 *
 * WHY THIS IS ENTERPRISE-GRADE
 *  • Save/Publish correctness does not depend on background autosave timing.
 *  • FormListsLab can remain fast and local while editing.
 *  • Only dirty option sets are flushed.
 *  • Existing form-builder routes that do not register a flusher are unaffected.
 *
 * PERFORMANCE CONTRACT
 *  • flushersRef is a ref-backed registry.
 *  • register/unregister mutate the ref only.
 *  • No provider re-render occurs when FormListsLabPage registers a flusher.
 *  • Work only happens when Save Draft / Publish explicitly calls
 *    flushFormListsDraftChanges(...).
 */

const buildFormListsUnsavedWorkingCopyStorageKey = (formId) => (
  `form_${formId || 'unknown'}_formListsLab_unsavedWorkingCopy`
);

const normalizeUnsavedWorkingCopyStatus = ({
  formId,
  previousStatus = null,
  source = 'unknown',
  reason = 'unknown',
} = {}) => {
  const previousSources =
    previousStatus &&
    typeof previousStatus === 'object' &&
    previousStatus.sources &&
    typeof previousStatus.sources === 'object'
      ? previousStatus.sources
      : {};

  const changedAt = Date.now();

  return {
    hasUnsavedChanges: true,
    formId: formId || null,
    changedAt,
    sources: {
      ...previousSources,
      [source]: true,
    },
    lastSource: source,
    lastReason: reason,
  };
};

const areUnsavedWorkingCopyStatusesEqual = (a, b) => (
  JSON.stringify(a || null) === JSON.stringify(b || null)
);

const FormListsDraftSyncContext = createContext({
  registerFormListsFlush: () => () => {},
  flushFormListsDraftChanges: async () => ({
    status: 'skipped',
    reason: 'provider_not_initialized',
    dropdownOptionsOverride: null,
    results: [],
  }),
  getFormListsUnsavedWorkingCopyStatus: () => ({
    hasUnsavedChanges: false,
    formId: null,
    changedAt: null,
    sources: {},
    lastSource: null,
    lastReason: null,
  }),
  markFormListsUnsavedWorkingCopy: () => ({
    status: 'skipped',
    reason: 'provider_not_initialized',
  }),
  clearFormListsUnsavedWorkingCopy: () => ({
    status: 'skipped',
    reason: 'provider_not_initialized',
  }),
});

export const FormListsDraftSyncProvider = ({ children }) => {
  const flushersRef = useRef(new Map());
  const unsavedWorkingCopyStatusRef = useRef(new Map());

  /**
   * Unsaved Form Lists working-copy status
   * ---------------------------------------------------------------------------
   * This is intentionally separate from spreadsheet dirty state.
   *
   * SPREADSHEET DIRTY
   *  • means GroupedSpreadsheetCanvas has local edits that have not yet been
   *    applied to builder-local form state.
   *
   * UNSAVED WORKING COPY
   *  • means Form Lists dropdown options and/or dependencies have been applied
   *    to builder-local state, but the form has not yet been saved as a draft
   *    or published.
   *
   * WHY THIS EXISTS
   *  • Route-exit flush should preserve values without making the user think
   *    those changes were saved to Firestore.
   *  • The Lists side-panel dirty badge must survive route navigation/remount.
   *  • Save Draft / Publish are the only successful persistence boundaries that
   *    should clear this status.
   *
   * PERFORMANCE RULE
   *  • This state changes only on boundary events or dependency checkbox edits.
   *  • It is not updated on every spreadsheet keystroke.
   */
  const [unsavedWorkingCopyVersion, setUnsavedWorkingCopyVersion] = useState(0);

  const registerFormListsFlush = useCallback(({
    id,
    formId,
    flush,
  } = {}) => {
    if (!id || typeof flush !== 'function') {
      console.warn('[FormListsDraftSyncContext] Invalid flusher registration skipped.', {
        id,
        formId,
        hasFlush: typeof flush === 'function',
      });

      return () => {};
    }

    const key = `${formId || 'unknown'}::${id}`;

    flushersRef.current.set(key, {
      id,
      formId: formId || null,
      flush,
    });

    return () => {
      flushersRef.current.delete(key);
    };
  }, []);

  const getFormListsUnsavedWorkingCopyStatus = useCallback(({
    formId,
  } = {}) => {
    if (!formId) {
      return {
        hasUnsavedChanges: false,
        formId: null,
        changedAt: null,
        sources: {},
        lastSource: null,
        lastReason: null,
      };
    }

    const cachedStatus = unsavedWorkingCopyStatusRef.current.get(formId);

    if (cachedStatus) {
      return cachedStatus;
    }

    const storageKey = buildFormListsUnsavedWorkingCopyStorageKey(formId);
    const storedStatus = safeGetItem(storageKey);

    if (
      storedStatus &&
      typeof storedStatus === 'object' &&
      storedStatus.hasUnsavedChanges === true
    ) {
      unsavedWorkingCopyStatusRef.current.set(formId, storedStatus);
      return storedStatus;
    }

    return {
      hasUnsavedChanges: false,
      formId,
      changedAt: null,
      sources: {},
      lastSource: null,
      lastReason: null,
    };
  }, []);

  const markFormListsUnsavedWorkingCopy = useCallback(({
    formId,
    source = 'unknown',
    reason = 'unknown',
  } = {}) => {
    if (!formId) {
      console.warn(
        '[FormListsDraftSyncContext] Cannot mark Form Lists working copy ' +
        'dirty because formId is missing.',
        {
          source,
          reason,
        }
      );

      return {
        status: 'skipped',
        reason: 'missing_formId',
      };
    }

    const storageKey = buildFormListsUnsavedWorkingCopyStorageKey(formId);
    const previousStatus =
      unsavedWorkingCopyStatusRef.current.get(formId) ||
      safeGetItem(storageKey) ||
      null;

    const previousSources =
      previousStatus &&
      typeof previousStatus === 'object' &&
      previousStatus.sources &&
      typeof previousStatus.sources === 'object'
        ? previousStatus.sources
        : {};

    /**
     * Lightweight duplicate guard
     * -------------------------------------------------------------------------
     * If this source is already marked dirty, do not rewrite the same status
     * object repeatedly.
     *
     * WHY
     *  • The sidepanel only needs to know that this form has unsaved Form Lists
     *    working-copy changes.
     *  • Repeated dependency checkbox edits should not churn storage if the
     *    dependency source is already marked.
     *  • A new source, such as dropdownOptions after dependencies, still updates
     *    the status snapshot so diagnostics remain useful.
     */
    if (
      previousStatus?.hasUnsavedChanges === true &&
      previousSources[source] === true
    ) {
      return {
        status: 'success',
        reason: 'already_marked',
        statusSnapshot: previousStatus,
      };
    }

    const nextStatus = normalizeUnsavedWorkingCopyStatus({
      formId,
      previousStatus,
      source,
      reason,
    });

    if (areUnsavedWorkingCopyStatusesEqual(previousStatus, nextStatus)) {
      return {
        status: 'success',
        reason: 'unchanged',
        statusSnapshot: nextStatus,
      };
    }

    unsavedWorkingCopyStatusRef.current.set(formId, nextStatus);
    safeSetItem(storageKey, nextStatus);
    setUnsavedWorkingCopyVersion((prev) => prev + 1);

    console.log('[FormListsDraftSyncContext] Marked Form Lists working copy dirty:', {
      formId,
      source,
      reason,
      sources: nextStatus.sources,
    });

    return {
      status: 'success',
      reason,
      statusSnapshot: nextStatus,
    };
  }, []);

  const clearFormListsUnsavedWorkingCopy = useCallback(({
    formId,
    reason = 'clear_unsaved_working_copy',
  } = {}) => {
    if (!formId) {
      console.warn(
        '[FormListsDraftSyncContext] Cannot clear Form Lists working copy ' +
        'dirty state because formId is missing.',
        {
          reason,
        }
      );

      return {
        status: 'skipped',
        reason: 'missing_formId',
      };
    }

    const storageKey = buildFormListsUnsavedWorkingCopyStorageKey(formId);

    unsavedWorkingCopyStatusRef.current.delete(formId);
    safeRemoveItem(storageKey);
    setUnsavedWorkingCopyVersion((prev) => prev + 1);

    console.log('[FormListsDraftSyncContext] Cleared Form Lists working copy dirty state:', {
      formId,
      reason,
    });

    return {
      status: 'success',
      reason,
    };
  }, []);

  const flushFormListsDraftChanges = useCallback(async ({
    formId,
    reason = 'external_flush',
  } = {}) => {
    const allFlushers = Array.from(flushersRef.current.values());

    const matchingFlushers = allFlushers.filter((entry) => {
      if (!formId) return true;
      return !entry.formId || entry.formId === formId;
    });

    if (matchingFlushers.length === 0) {
      return {
        status: 'skipped',
        reason: 'no_registered_form_lists_flushers',
        dropdownOptionsOverride: null,
        results: [],
      };
    }

    const results = [];
    let mergedDropdownOptionsOverride = null;

    for (const entry of matchingFlushers) {
      try {
        const result = await Promise.resolve(entry.flush({
          reason,
          formId,
        }));

        results.push({
          id: entry.id,
          formId: entry.formId,
          ...(result || {}),
        });

        if (
          result?.dropdownOptionsOverride &&
          typeof result.dropdownOptionsOverride === 'object'
        ) {
          mergedDropdownOptionsOverride = {
            ...(mergedDropdownOptionsOverride || {}),
            ...result.dropdownOptionsOverride,
          };
        }
      } catch (error) {
        console.error('[FormListsDraftSyncContext] Form Lists flusher failed:', {
          id: entry.id,
          formId: entry.formId,
          reason,
          error,
        });

        results.push({
          id: entry.id,
          formId: entry.formId,
          status: 'error',
          reason,
          error,
          message: error?.message || String(error),
        });
      }
    }

    const hasErrors = results.some((result) => result.status === 'error');

    return {
      status: hasErrors ? 'error' : 'success',
      reason,
      dropdownOptionsOverride: mergedDropdownOptionsOverride,
      results,
    };
  }, []);

  const value = useMemo(() => ({
    registerFormListsFlush,
    flushFormListsDraftChanges,
    getFormListsUnsavedWorkingCopyStatus,
    markFormListsUnsavedWorkingCopy,
    clearFormListsUnsavedWorkingCopy,
  }), [
    registerFormListsFlush,
    flushFormListsDraftChanges,
    getFormListsUnsavedWorkingCopyStatus,
    markFormListsUnsavedWorkingCopy,
    clearFormListsUnsavedWorkingCopy,
    unsavedWorkingCopyVersion,
  ]);

  return (
    <FormListsDraftSyncContext.Provider value={value}>
      {children}
    </FormListsDraftSyncContext.Provider>
  );
};

export const useFormListsDraftSync = () => (
  useContext(FormListsDraftSyncContext)
);